import React, { Suspense } from 'react'
import AnalyticsHeader from '../AnalyticsHeader'
import AnalyticsHeaderSkeleton from '../skeleton/AnalyticsHeaderSkeleton'
import { useTranslation } from 'react-i18next'
import { ReactComponent as PreviousIcon } from '../../../assets/svg/PreviousIcon.svg'
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom.min'
import PulseFilterContainer from './PulseFilterContainer'
import { useState, useEffect } from 'react'
import PulseCategoryList from './PulseCategoryList'
import { mapGroupsToCategories, mapGroupsToDeltas, groupKeys, comparisonLabel } from '../../../constants/PulseCheckAnalyticsConstants'
import { calculateDateRange } from '../../../utils/dateUtils'
import { getSurveyAnalytics, downloadSurveyAnalyticsReport } from '../../../server/server'
import VsLastWeekCard from './VsLastWeekCard'
import TrendsCard from './TrendsCard'
import NeedsAttentionCard from './NeedsAttentionCard'
import HeatMapCard from './HeatMapCard'
import PulseCheckSkeleton from '../skeleton/PulseCheckSkeleton'

const defaultStartDate = () => {
    const date = new Date()
    date.setDate(date.getDate() - 90)
    return date
}

function PulseCheckScreen() {
    const { t, i18n } = useTranslation()
    const history = useHistory()
    const { surveyId } = useParams()
    const [startDate, setStartDate] = useState(defaultStartDate())
    const [endDate, setEndDate] = useState(new Date())
    const [rangeChanged, setRangeChanged] = useState(new Date())
    const [mode, setMode] = useState('year')
    const [targetGroup, setTargetGroup] = useState({
        text: 'All target groups',
        target: [],
        unfilteredTarget: [],
    })
    const [analytics, setAnalytics] = useState(null)
    const [loading, setLoading] = useState(true)
    const [downloading, setDownloading] = useState(false)
    // The heatmap's own Student/Class/Year toggle, surfaced so the PDF matches the view.
    const [heatmapView, setHeatmapView] = useState('year')

    useEffect(() => {
        loadAnalytics()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveyId, rangeChanged, targetGroup])

    const loadAnalytics = async () => {
        if (!surveyId || !startDate || !endDate) {
            setAnalytics(null)
            setLoading(false)
            return
        }
        setLoading(true)
        try {
            const { startDate: start, endDate: end } = calculateDateRange({ startDate, endDate })
            const resp = await getSurveyAnalytics({
                survey_id: surveyId,
                selected_filters: { selected: [], all: true },
                user_ids: targetGroup?.target || [], // resolved student ids from the modal; [] => all
                start_date: start,
                end_date: end,
            })
            setAnalytics(resp.status === 200 ? resp.data : null)
        } catch (err) {
            console.error('Failed to load pulse check analytics', err)
            setAnalytics(null)
        } finally {
            setLoading(false)
        }
    }

    // Downloads the current view as a PDF, using the exact same filters as loadAnalytics
    // so the report always matches what's on screen.
    const handleDownload = async () => {
        if (downloading || loading || !analytics || !surveyId || !startDate || !endDate) {
            return
        }
        setDownloading(true)
        try {
            const { startDate: start, endDate: end } = calculateDateRange({ startDate, endDate })
            const resp = await downloadSurveyAnalyticsReport({
                survey_id: surveyId,
                selected_filters: { selected: [], all: true },
                user_ids: targetGroup?.target || [],
                start_date: start,
                end_date: end,
                locale: i18n?.language || 'en',
                view: heatmapView, // 'year' | 'class' | 'student' — matches the heatmap's current view
            })
            if (resp?.status !== 200 || !resp?.data) {
                console.error('Failed to download pulse check report', resp)
                return
            }
            const blob = new Blob([resp.data], { type: resp.contentType || 'application/pdf' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `${surveyTitle || 'pulse-check'}.pdf`
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (err) {
            console.error('Failed to download pulse check report', err)
        } finally {
            setDownloading(false)
        }
    }

    const categories = mapGroupsToCategories(analytics?.groups, analytics?.trend)
    const deltaGroups = mapGroupsToDeltas(analytics?.groups, analytics?.trend)
    const trendGroups = groupKeys(analytics)
    const summary = analytics?.summary
    const compareLabel = comparisonLabel(analytics?.range?.granularity)
    // Prefer the survey's own title from the analytics payload; fall back to the generic label.
    const surveyTitle = analytics?.survey?.title || analytics?.title || summary?.title || t('pulseCheck')

    const goBack = () => {
        history.goBack()
    }
    const handleDateSelect = (date) => {
        if (startDate !== null && endDate !== null) {
            setStartDate(date)
            setEndDate(null)
            return
        }
        if (startDate !== null && endDate === null && startDate <= date) {
            setEndDate(date)
            return
        }
        if (startDate !== null && endDate === null && startDate > date) {
            setStartDate(date)
            return
        }
    }

    const handleLastMonth = (start, end) => {
        setStartDate(start)
        setEndDate(end)
    }

    const updateAnalytics = () => {
        if (startDate !== null && endDate !== null) {
            setRangeChanged(new Date())
            return
        }
    }

    const handleModeChange = (newMode) => {
        setMode(newMode)

        if (newMode === 'class') {
            setTargetGroup({
                text: 'All target groups',

                target: [],

                unfilteredTarget: [],
            })
        } else {
            setTargetGroup({
                text: 'All years',

                target: [],

                unfilteredTarget: [],
            })
        }
    }

    return (
        <div className="page-shell bg-lightBG">
            <div className="analytics-header">
                <Suspense fallback={<AnalyticsHeaderSkeleton />}>
                    <AnalyticsHeader title={t('analytics')} subtitle={t('getDeeperInsights')} />
                </Suspense>
            </div>
            <div className="flex flex-row items-center justify-between mt-6">
                <button aria-label={t('back')} className="min-w-[54px] h-[54px] bg-[#5E639B] rounded-full flex items-center justify-center shadow-customBlue " onClick={goBack}>
                    <PreviousIcon style={{ color: 'white' }} />
                </button>

                <p className="text-[26px] leading-8 text-darkenPurple font-bold">{surveyTitle}</p>
                <div className="min-w-[54px]"></div>
            </div>
            <PulseFilterContainer
                startDate={startDate}
                endDate={endDate}
                handleDateSelect={handleDateSelect}
                handleLastMonth={handleLastMonth}
                updateAnalytics={updateAnalytics}
                mode={mode}
                setMode={handleModeChange}
                targetGroup={targetGroup}
                setTargetGroup={setTargetGroup}
                onDownload={handleDownload}
                downloading={downloading}
                downloadDisabled={loading || !analytics || downloading}
            />
            {loading ? (
                <PulseCheckSkeleton />
            ) : (
                <>
                    <PulseCategoryList items={categories} compareLabel={compareLabel} />
                    <div className="flex flex-row w-full gap-4 mt-5">
                        <TrendsCard trend={analytics?.trend || []} groups={trendGroups} />
                        <VsLastWeekCard groups={deltaGroups} compareLabel={compareLabel} />
                    </div>
                    <div className="flex flex-row w-full gap-4 mt-5">
                        <HeatMapCard heatmap={analytics?.heatmap} groups={trendGroups} view={mode} onViewChange={setHeatmapView} />
                        <NeedsAttentionCard students={analytics?.heatmap?.student || []} />
                    </div>
                </>
            )}
        </div>
    )
}

export default PulseCheckScreen
